import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from './api';

/**
 * Process the session_id from URL fragment (returned by Emergent OAuth).
 * Must run BEFORE any /auth/me check.
 */
export const useAuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setStatus('no_session');
      return;
    }
    const sessionId = match[1];

    authApi
      .exchangeSession(sessionId)
      .then((res) => {
        // Clean URL fragment
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/admin', { replace: true, state: { user: res.data } });
      })
      .catch((err) => {
        console.error('Auth exchange failed', err);
        const detail = err.response?.data?.detail || 'Erreur de connexion';
        setStatus('error');
        navigate('/admin/login', { replace: true, state: { error: detail } });
      });
  }, [location.hash, navigate]);

  return status;
};

/** Hook for protected routes — returns { user, loading } */
export const useRequireAdmin = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user came from AuthCallback, accept it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }
    // CRITICAL: skip /me when an OAuth callback is in progress
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    authApi
      .me()
      .then((res) => {
        if (res.data.is_admin) {
          setUser(res.data);
        } else {
          navigate('/admin/login', { replace: true });
        }
      })
      .catch(() => {
        navigate('/admin/login', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate, location.state]);

  return { user, loading };
};

/**
 * Trigger Google login.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/admin';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
